import { useEffect, useRef } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';

function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;
      const scrollY = window.scrollY;
      const opacity = Math.max(0, 1 - scrollY / 600);
      heroRef.current.style.opacity = String(opacity);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-[80vh] flex items-center overflow-hidden bg-gp-graphite"
    >
      <div className="absolute inset-0">
        <img
          src="/images/EQUIPAMENTOS-E-COMPONENTES/NOSSOS-PRODUTOS/ACUMULADORES-HIDRAULICOS/HERO/HERO-ACUMULADORES-HIDRAULICOS.webp"
          alt="Acumuladores hidráulicos industriais em armazenamento"
          className="w-full h-full object-cover opacity-45"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-gp-graphite via-gp-graphite/70 to-gp-graphite/50"></div>
      <div className="absolute inset-0 bg-gradient-to-t from-gp-graphite via-transparent to-transparent"></div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-24">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">DURAFLOW</span>
          </div>

          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white uppercase leading-[0.95] mb-8">
            Acumuladores<br />
            <span className="text-gp-yellow">Hidráulicos</span>
          </h1>

          <p className="text-gray-300 text-lg md:text-xl max-w-xl leading-relaxed mb-10">
            Soluções para armazenamento de energia hidráulica, absorção de choques, estabilização de pressão e aumento da confiabilidade operacional de sistemas hidráulicos industriais.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="https://wa.me/5527993092922"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
            >
              <div className="w-9 h-9 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
                <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
              </div>
              <span>Solicitar Cotação</span>
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
            </a>
            <a
              href="https://wa.me/5527993092922"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 border border-gp-yellow text-gp-yellow hover:bg-gp-yellow hover:text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 whitespace-nowrap"
            >
              <span>Falar com Especialista</span>
              <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
            </a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <a href="#descricao" className="text-gray-500 hover:text-gp-yellow transition-colors duration-300">
          <i className="ri-arrow-down-line text-2xl w-8 h-8 flex items-center justify-center"></i>
        </a>
      </div>
    </section>
  );
}

function DescriptionSection() {
  return (
    <section id="descricao" className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Sobre a Categoria</span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>
          <p className="text-gray-300 text-lg md:text-xl leading-relaxed">
            Acumuladores hidráulicos são dispositivos de armazenamento de energia que utilizam gás comprimido para manter a pressão do sistema, absorver picos de pressão, compensar vazamentos e fornecer fluxo adicional em momentos de alta demanda. São componentes essenciais para a estabilidade e segurança de circuitos hidráulicos industriais.
          </p>
        </div>
      </div>
    </section>
  );
}

function MidCTASection() {
  return (
    <section className="relative bg-gp-graphite py-16 md:py-20 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <h3 className="font-display text-2xl md:text-3xl font-bold text-white uppercase tracking-wider mb-4">
            Precisa dimensionar ou cotar um acumulador hidráulico?
          </h3>
          <p className="text-gray-300 text-base md:text-lg leading-relaxed mb-8">
            Envie os dados de pressão, volume, fluido e aplicação para nossa equipe técnica analisar e recomendar a melhor solução.
          </p>
          <a
            href="https://wa.me/5527993092922"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-sm font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <div className="w-9 h-9 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
              <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
            </div>
            <span>Enviar Dados do Acumulador</span>
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

function ApplicationsSection() {
  const items = [
    'Papel e Celulose',
    'Mineração',
    'Siderurgia',
    'Portos',
    'Energia',
    'Indústria de Processo',
    'Máquinas Industriais',
    'Equipamentos Móveis',
  ];

  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Duraflow</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Aplicações Industriais
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {items.map((item, index) => (
              <div
                key={index}
                className="group flex items-center gap-4 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg px-6 py-5 transition-all duration-300 hover:bg-gp-graphite-lighter/50"
              >
                <div className="w-10 h-10 bg-gp-yellow/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-check-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                </div>
                <span className="text-gray-200 text-sm md:text-base font-medium">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TypesSection() {
  const types = [
    {
      title: 'Acumuladores de Bexiga',
      description:
        'Utilizam uma bexiga flexível de elastômero para separar o gás do fluido hidráulico. São os mais comuns na indústria, com excelente resposta dinâmica, ampla faixa de volumes e facilidade de manutenção. Ideais para absorção de pulsação, amortecimento de choques e armazenamento de energia em sistemas com ciclos rápidos.',
    },
    {
      title: 'Acumuladores de Pistão',
      description:
        'Utilizam um pistão flutuante com selagem para separar gás e fluido. Suportam pressões mais elevadas e maiores volumes, com excelente resistência a condições severas de operação. Recomendados para sistemas de alta pressão, grandes variações de temperatura e aplicações que exigem durabilidade extrema.',
    },
    {
      title: 'Acumuladores de Diafragma',
      description:
        'Utilizam um diafragma flexível como elemento separador. São compactos, leves e de resposta ultra-rápida. Ideais para volumes menores, aplicações de amortecimento de vibrações, compensação de pequenos vazamentos e sistemas onde o espaço de instalação é limitado.',
    },
  ];

  return (
    <section className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Nosso Portfólio</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Tipos de Acumuladores
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {types.map((type, index) => (
              <div
                key={index}
                className="group relative bg-gp-graphite-light border border-gp-graphite-lighter hover:border-gp-yellow/50 rounded-lg p-8 transition-all duration-500 hover:-translate-y-2"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gp-yellow scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left rounded-t-lg"></div>
                <div className="w-14 h-14 bg-gp-yellow/10 rounded-lg flex items-center justify-center mb-6 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-settings-3-line text-gp-yellow w-7 h-7 flex items-center justify-center"></i>
                </div>
                <h3 className="text-white font-semibold text-lg md:text-xl mb-3">{type.title}</h3>
                <p className="text-gray-400 text-sm md:text-base leading-relaxed">{type.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function BenefitsSection() {
  const benefits = [
    'Redução de golpes de aríete',
    'Absorção de picos de pressão',
    'Proteção de bombas e válvulas',
    'Estabilização do sistema hidráulico',
    'Reserva de energia hidráulica',
    'Melhoria da eficiência operacional',
    'Aumento da vida útil dos componentes',
  ];

  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Por que Duraflow</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Benefícios
            </h2>
          </div>

          <div className="space-y-4">
            {benefits.map((item, index) => (
              <div
                key={index}
                className="group flex flex-col md:flex-row md:items-start gap-4 bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg p-6 md:p-8 transition-all duration-300 hover:bg-gp-graphite-lighter/30"
              >
                <div className="w-12 h-12 bg-gp-yellow/10 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                  <i className="ri-add-line text-gp-yellow w-6 h-6 flex items-center justify-center"></i>
                </div>
                <p className="text-gray-300 text-sm md:text-base leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function SegmentsSection() {
  const segments = [
    { name: 'Papel e Celulose', icon: 'ri-building-2-line' },
    { name: 'Mineração', icon: 'ri-building-2-line' },
    { name: 'Siderurgia', icon: 'ri-building-2-line' },
    { name: 'Portos e Logística', icon: 'ri-building-2-line' },
    { name: 'Energia e Geração', icon: 'ri-building-2-line' },
    { name: 'Indústria de Processo', icon: 'ri-building-2-line' },
    { name: 'Máquinas e Equipamentos', icon: 'ri-building-2-line' },
    { name: 'Infraestrutura Industrial', icon: 'ri-building-2-line' },
  ];

  return (
    <section className="relative bg-gp-graphite py-20 md:py-28 overflow-hidden">
      <div className="w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Aplicações</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Setores de <span className="text-gp-yellow">Aplicação</span>
            </h2>
            <p className="text-gray-400 text-sm md:text-base max-w-2xl mx-auto mt-4 leading-relaxed">
              Os acumuladores hidráulicos são aplicados em diversas indústrias que exigem estabilidade, segurança e alto desempenho em circuitos hidráulicos de missão crítica.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {segments.map((seg, index) => (
              <div
                key={index}
                className="flex items-center gap-4 bg-gp-graphite-light border border-gp-graphite-lighter rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300"
              >
                <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                  <i className={`${seg.icon} text-gp-yellow w-5 h-5 flex items-center justify-center`}></i>
                </div>
                <span className="text-gray-300 text-sm font-medium">{seg.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const faqs = [
    {
      question: 'O que é um acumulador hidráulico?',
      answer: 'Um acumulador hidráulico é um dispositivo de armazenamento de energia que utiliza gás comprimido (geralmente nitrogênio) para manter a pressão do sistema hidráulico, absorver picos de pressão e fornecer fluxo adicional quando necessário. Ele atua como uma bateria hidráulica, armazenando fluido sob pressão e liberando-o conforme a demanda do sistema.',
    },
    {
      question: 'Para que serve um acumulador hidráulico?',
      answer: 'O acumulador hidráulico tem múltiplas funções: armazenamento de energia para uso em picos de demanda, absorção de choques e pulsação em sistemas com bombas de deslocamento positivo, compensação de vazamentos, manutenção de pressão durante ciclos longos, amortecimento de golpes de aríete e proteção de componentes contra sobrepressão. É um componente essencial para a estabilidade e confiabilidade de circuitos hidráulicos industriais.',
    },
    {
      question: 'Qual a diferença entre acumulador de bexiga e pistão?',
      answer: 'O acumulador de bexiga utiliza uma bexiga flexível de elastômero como elemento separador entre gás e fluido hidráulico, oferecendo excelente resposta dinâmica, menor peso e facilidade de manutenção. Já o acumulador de pistão utiliza um pistão metálico flutuante com múltiplas vedações, suportando pressões mais elevadas, temperaturas extremas e maiores volumes. A escolha depende da aplicação, condições operacionais e requisitos de manutenção.',
    },
    {
      question: 'Como dimensionar um acumulador hidráulico?',
      answer: 'O dimensionamento considera: pressão de pré-carga do gás (P0), pressão mínima e máxima de operação (P1 e P2), volume útil de fluido necessário, temperatura de operação e tipo de aplicação (absorção de choque, armazenamento de energia ou compensação). O cálculo utiliza a relação entre pressões e volumes, assumindo compressão/expansão adiabática ou isotérmica do gás conforme a velocidade do ciclo. Nossa equipe técnica pode auxiliar no dimensionamento correto para sua aplicação.',
    },
    {
      question: 'Quando substituir um acumulador hidráulico?',
      answer: 'A substituição é recomendada quando há: perda de pré-carga frequente indicando falha na bexiga ou nas vedações do pistão, deformação visível do corpo, corrosão significativa, vazamento externo, falha em testes hidrostáticos, ou quando o acumulador atinge o fim da vida útil recomendada pelo fabricante. Inspeções periódicas da bexiga, vedações e válvula de gás são essenciais para garantir a segurança e performance.',
    },
    {
      question: 'Quais indústrias utilizam acumuladores hidráulicos?',
      answer: 'Acumuladores hidráulicos são amplamente utilizados nas indústrias de papel e celulose (prensas, sistemas de alimentação, enroladeiras), mineração (britadores, correias transportadoras), siderurgia (laminadores, sistemas de avanço), portos (guinchos, sistemas de giro), energia (turbinas, sistemas de segurança), além de máquinas industriais, prensas hidráulicas, equipamentos móveis e plataformas de elevação.',
    },
  ];

  return (
    <section className="relative bg-gp-graphite-light py-20 md:py-28 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(232,185,42,0.3) 1px, transparent 0)',
            backgroundSize: '40px 40px',
          }}
        ></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-3 mb-4">
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
              <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Perguntas Frequentes</span>
              <div className="w-12 h-[2px] bg-gp-yellow"></div>
            </div>
            <h2 className="font-display text-3xl md:text-5xl font-bold text-white uppercase tracking-wider">
              Dúvidas sobre <span className="text-gp-yellow">Acumuladores</span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details
                key={index}
                className="group bg-gp-graphite border border-gp-graphite-lighter hover:border-gp-yellow/40 rounded-lg transition-all duration-300"
              >
                <summary className="flex items-center justify-between cursor-pointer p-6 md:p-8 list-none">
                  <h3 className="text-white font-semibold text-base md:text-lg pr-8 leading-relaxed">
                    {faq.question}
                  </h3>
                  <div className="w-8 h-8 bg-gp-yellow/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-gp-yellow/20 transition-colors duration-300">
                    <i className="ri-add-line text-gp-yellow w-5 h-5 flex items-center justify-center group-open:hidden"></i>
                    <i className="ri-subtract-line text-gp-yellow w-5 h-5 hidden group-open:flex items-center justify-center"></i>
                  </div>
                </summary>
                <div className="px-6 md:px-8 pb-6 md:pb-8">
                  <p className="text-gray-400 text-sm md:text-base leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden bg-gp-graphite">
      <div className="absolute inset-0">
        <img
          src="/images/EQUIPAMENTOS-E-COMPONENTES/duraflow-cta.jpg"
          alt="Ambiente industrial Duraflow"
          className="w-full h-full object-cover opacity-40"
        />
        <div className="absolute inset-0 bg-gp-graphite/70"></div>
      </div>

      <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 py-20">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
            <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Atendimento Técnico</span>
            <div className="w-12 h-[2px] bg-gp-yellow"></div>
          </div>

          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-bold text-white uppercase tracking-wider mb-6">
            Precisa de um
            <br />
            <span className="text-gp-yellow">Orçamento?</span>
          </h2>

          <p className="text-gray-300 text-lg md:text-xl max-w-2xl mx-auto mb-10 leading-relaxed">
            Nossa equipe técnica está pronta para entender sua aplicação e indicar a melhor solução em acumuladores hidráulicos para seu sistema.
          </p>

          <a
            href="https://wa.me/5527993092922"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap"
          >
            <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center group-hover:bg-white transition-colors duration-300">
              <i className="ri-whatsapp-line text-gp-yellow group-hover:text-gp-graphite w-5 h-5 flex items-center justify-center transition-colors duration-300"></i>
            </div>
            <span>Chamar no WhatsApp</span>
            <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
          </a>
        </div>
      </div>
    </section>
  );
}

export default function AcumuladoresPage() {
  useSeo({
    title: 'Acumuladores Hidráulicos Industriais | Duraflow | Grupo Pilar',
    description: 'Fornecimento de acumuladores hidráulicos industriais para sistemas de alta pressão, amortecimento de pulsação, armazenamento de energia hidráulica e estabilização de circuitos hidráulicos.',
    keywords: 'acumuladores hidráulicos, acumulador hidráulico industrial, acumulador de bexiga, acumulador de pistão, acumulador hidráulico para celulose, acumulador hidráulico para indústria, duraflow acumuladores hidráulicos',
    canonical: 'https://www.pilarmanutencao.com.br/acumuladores-hidraulicos',
    ogUrl: 'https://www.pilarmanutencao.com.br/acumuladores-hidraulicos',
    ogImage: '/images/EQUIPAMENTOS-E-COMPONENTES/NOSSOS-PRODUTOS/ACUMULADORES-HIDRAULICOS/CARD/ACUMULADOR-HIDRAULICO.webp',
  });

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'O que é um acumulador hidráulico?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Um acumulador hidráulico é um dispositivo de armazenamento de energia que utiliza gás comprimido (geralmente nitrogênio) para manter a pressão do sistema hidráulico, absorver picos de pressão e fornecer fluxo adicional quando necessário.',
        },
      },
      {
        '@type': 'Question',
        name: 'Para que serve um acumulador hidráulico?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O acumulador hidráulico tem múltiplas funções: armazenamento de energia para uso em picos de demanda, absorção de choques e pulsação, compensação de vazamentos, manutenção de pressão durante ciclos longos e amortecimento de golpes de aríete.',
        },
      },
      {
        '@type': 'Question',
        name: 'Qual a diferença entre acumulador de bexiga e pistão?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O acumulador de bexiga utiliza uma bexiga flexível de elastômero como elemento separador, oferecendo excelente resposta dinâmica e facilidade de manutenção. O acumulador de pistão utiliza um pistão metálico flutuante, suportando pressões mais elevadas, temperaturas extremas e maiores volumes.',
        },
      },
      {
        '@type': 'Question',
        name: 'Como dimensionar um acumulador hidráulico?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'O dimensionamento considera pressão de pré-carga do gás (P0), pressão mínima e máxima de operação (P1 e P2), volume útil de fluido necessário, temperatura de operação e tipo de aplicação. Nossa equipe técnica pode auxiliar no dimensionamento correto.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quando substituir um acumulador hidráulico?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A substituição é recomendada quando há perda de pré-carga frequente, deformação visível do corpo, corrosão significativa, vazamento externo, falha em testes hidrostáticos ou fim da vida útil recomendada pelo fabricante.',
        },
      },
      {
        '@type': 'Question',
        name: 'Quais indústrias utilizam acumuladores hidráulicos?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Acumuladores são utilizados nas indústrias de papel e celulose, mineração, siderurgia, portos, energia, máquinas industriais, prensas hidráulicas, equipamentos móveis e plataformas de elevação.',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: 'https://www.pilarmanutencao.com.br/',
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'Duraflow',
        item: 'https://www.pilarmanutencao.com.br/equipamentos-e-componentes',
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: 'Acumuladores Hidráulicos',
        item: 'https://www.pilarmanutencao.com.br/acumuladores-hidraulicos',
      },
    ],
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Acumuladores Hidráulicos Industriais',
    description: 'Fornecimento de acumuladores hidráulicos industriais para sistemas de alta pressão, amortecimento de pulsação, armazenamento de energia hidráulica e estabilização de circuitos hidráulicos.',
    category: 'Acumuladores Hidráulicos',
    brand: {
      '@type': 'Brand',
      name: 'DURAFLOW',
    },
    manufacturer: {
      '@type': 'Organization',
      name: 'Grupo Pilar',
    },
  };

  return (
    <div className="min-h-screen bg-gp-graphite">
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(productSchema) }}
      />

      <Navbar />
      <main>
        <HeroSection />
        <DescriptionSection />
        <MidCTASection />
        <ApplicationsSection />
        <TypesSection />
        <BenefitsSection />
        <SegmentsSection />
        <FAQSection />
        <CTASection />
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}